/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, EventEmitter, Inject, Input, Output } from '@angular/core';

import { SlicedDataSource, SortDirection } from '@gh/core-data';
import { UI_ERROR_HANDLER, UiErrorHandler } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';

import {
  AgGridEvent,
  Events,
  GridApi,
  GridOptions,
  IDatasource,
  IGetRowsParams,
  InfiniteRowModel,
  RowNode,
} from 'ag-grid-community';
import { assign, get, isNil } from 'lodash';

import { GridSelectedRowChangeEvent } from '../../grid-events';
import { FocusedCell } from '../../state/grid-state.interfaces';
import { GRID_FEATURE, GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';

export interface GridInfiniteScroll {
  refresh(): void;
}

export interface GridRowTotalChangeEvent {
  total: number;
}

export interface GridDataExistEvent {
  exists: boolean;
}

/**
 * This directive encapsulates infinite scroll behaviour of grid component
 */
@Directive({
  selector: '[ghGridInfiniteScroll]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridInfiniteScrollDirective,
    multi: true,
  }],
  exportAs: 'ghGridInfiniteScroll',
})
export class GridInfiniteScrollDirective implements GridFeature, GridInfiniteScroll {

  @Input() sorting?: boolean;

  @Output() focusedCellChange = new EventEmitter<FocusedCell>();
  @Output() selectedRowChange = new EventEmitter<GridSelectedRowChangeEvent<any>>();
  // emits after UI is refreshed
  @Output() updateComplete = new EventEmitter<AgGridEvent>();
  @Output() rowTotalChange = new EventEmitter<GridRowTotalChangeEvent>();
  @Output() dataExistChange = new EventEmitter<GridDataExistEvent>();

  private _selectedRow?: any;
  private _dataSource: SlicedDataSource<any>;
  private ready: boolean = false;
  private api: GridApi;
  private ignoreModelUpdatedEvents: boolean = false;
  private _total: number = -1;

  constructor(@Inject(UI_ERROR_HANDLER) private uiErrorHandler: UiErrorHandler) {

  }

  @Input()
  set dataSource(dataSource: SlicedDataSource<any>) {
    this._dataSource = dataSource;

    if (this.ready) {
      this.reset();
    }
  }

  @Input()
  set selectedRow(row: number) {
    if (row && row !== this._selectedRow) {
      throw new Error('Selection is not supported by infinite scroll');
    }
  }

  enhanceGridOptions(options: GridOptions): void {
    options.defaultColDef = {
      ...options.defaultColDef,
      sortable: isNotNil(this.sorting) ? this.sorting : get(options.defaultColDef, 'sortable', true),
    };
    options.rowModelType = 'infinite';
    options.maxConcurrentDatasourceRequests = 1;
  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  onGridReady(params: GridFeatureReadyParams): void {
    const { api } = params;

    assign(this, { api });

    this.ready = true;

    api.addEventListener(Events.EVENT_ROW_SELECTED, this.onRowSelected.bind(this));
    api.addEventListener(Events.EVENT_MODEL_UPDATED, (event: AgGridEvent) => {
      if (!this.ignoreModelUpdatedEvents) {
        this.updateComplete.emit(event);
      }
    });

    this.reset();
  }

  onDestroy(): void {
    // nothing to do
  }

  refresh(): void {
    this.ignoreModelUpdatedEvents = true;

    const model = <InfiniteRowModel>this.api.getModel();
    model.purgeCache();
    model.getRow(0);
  }

  private reset(): void {
    const dataSource = this._dataSource;

    if (dataSource) {
      this.api.setDatasource(this.wrapDataSource(dataSource));
      this.emitRowTotal(-1);
    }
  }

  private wrapDataSource(dataSource: SlicedDataSource<any>): IDatasource {
    const getRows = (params: IGetRowsParams) => {
      const sortModel = params.sortModel[0];

      this.api.showLoadingOverlay();
      this.ignoreModelUpdatedEvents = true;
      const query = {
        from: params.startRow,
        to: params.endRow,
        sorting: sortModel ? {
          field: sortModel.colId,
          direction: sortModel.sort === 'desc' ? SortDirection.Descending : SortDirection.Ascending,
        } : void 0,
      };
      dataSource.select(query).subscribe(
        (slicedData) => {
          this.ignoreModelUpdatedEvents = false;
          this.api.hideOverlay();
          if (slicedData.total === 0) {
            this.api.showNoRowsOverlay();
          }
          params.successCallback(slicedData.data, slicedData.total);
          this.emitRowTotal(slicedData.total);
        },
        (error) => {
          this.ignoreModelUpdatedEvents = false;
          this.api.hideOverlay();
          params.failCallback();
          this.uiErrorHandler.raise(error);
        },
      );
    };

    return { getRows };
  }

  private onRowSelected({ node }: { node: RowNode }): void {
    if (node.isSelected()) {
      if (node.id !== this._selectedRow) {
        this._selectedRow = node.id;
        this.selectedRowChange.emit({ id: node.id, node, data: node.data });
      }
    } else if (node.id === this._selectedRow) {
      const rowModel = this.api.getModel();
      const nextNode = rowModel.getRow(node.rowIndex) || rowModel.getRow(node.rowIndex - 1);
      if (nextNode && nextNode !== node) {
        nextNode.setSelected(true);
      } else if (isNil(nextNode)) {
        this._selectedRow = node.id;
        this.selectedRowChange.emit({});
      }
    }
  }

  private emitRowTotal(total: number): void {
    if (total > 0 && this._total <= 0) {
      this.dataExistChange.emit({ exists: true });
    } else if (total <= 0 && this._total > 0) {
      this.dataExistChange.emit({ exists: false });
    }
    if (this._total !== total) {
      this._total = total;
      this.rowTotalChange.emit({ total });
    }
  }
}
